import React from "react";
//#region types
import { WintSuperkollAPIModelAccounting, WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { UtilFormTypes } from "models/formTypes";
//#endregion
//#region components
import { useAccountService } from "serviceHooks";
import TransactionsAmount from "components/accounting/transactionsAmount";
//#endregion
import { DateHelper, FinancialReportHelper } from "utils/helpers";

/**
 * Monthly sibling of {@link useTransactionAmount}. The monthly reports differ from the
 * standard reports in two ways that prevent reuse of that hook directly:
 *  - the displayed value is the per-month column value (e.g. `uB`), not `amount`;
 *  - the filter is month-based (`startMonth`/`endMonth`) and carries no `columns[].range`,
 *    so each column's booking-date range is derived from `startMonth + repeatIndex`.
 *
 * Candidate for later unification with `useTransactionAmount` (tracked for design review).
 */
function useMonthlyTransactionAmount(
  filter?: WintSuperkollAPIModelFinancialReport.FinancialReportMonthlyFilter,
  valueSelector?: (column: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) => number | undefined,
  currency?: string
) {
  const accountService = useAccountService();
  const [accounts, setAccounts] = React.useState<WintSuperkollAPIModelAccounting.Account[]>([]);

  React.useEffect(() => {
    accountService.getAccounts({} as WintSuperkollAPIModelAccounting.AccountFilter).then((res) => {
      setAccounts(res?.items ?? []);
    });
  }, []);

  const getMonthRange = (repeatIndex: number): UtilFormTypes.DateRange | undefined => {
    if (!filter?.startMonth) return undefined;
    const monthDate = DateHelper.addMonths(DateHelper.getDateFromAPIYearMonth(filter.startMonth), repeatIndex);
    return {
      start: DateHelper.getFirstDayOfMonth(monthDate),
      end: DateHelper.getLastDayOfMonth(monthDate),
    };
  };

  const transactionAmountFormatter = (
    row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: WintSuperkollAPIModelFinancialReport.FinancialReportRow
  ) => {
    const account = accounts.find((ac) => ac.id === parentRow?.id);
    const dateRange = getMonthRange(repeatIndex ?? 0);

    return (
      <TransactionsAmount
        amount={valueSelector ? (valueSelector(row) ?? 0) : row.uB}
        currency={currency}
        categoryName={parentRow?.description}
        categoryId={FinancialReportHelper.getCategoryId(parentRow)}
        isAccount={parentRow?.isAccount}
        accountNumber={parentRow?.isAccount && account ? account.number : undefined}
        accountId={parentRow?.isAccount ? parentRow.id : undefined}
        dateRange={dateRange}
      />
    );
  };

  return { transactionAmountFormatter };
}

export default useMonthlyTransactionAmount;
